import { Asset } from '@/api/generatedApi';
import { useDeleteSupplementalAssets } from '@/hooks/api/assets/useDeleteSupplementalAssets';
import { useGetSupplementalAssets } from '@/hooks/api/assets/useGetSupplementalAssets';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetFileName } from '@/utils/assets';
import { Box, CircularProgress, IconButton, Skeleton, Tooltip } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Delete } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles(() => ({
  SupplementalAssetsList_TreeView: {
    paddingLeft: '.5rem',
    '& .MuiTreeItem-iconContainer': {
      display: 'none',
    },
  },
  SupplementalAssetsList_label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 0',
    height: '1.5rem',
  },
  SupplementalAssetsList_text: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    fontSize: '0.875rem',
    color: 'inherit',
  },
  SupplementalAssetsList_deleteButton: {
    padding: '4px',
    marginLeft: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  SupplementalAssetsList_filename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    maxWidth: '100%',
  },
  SupplementalAssetsList_LoadState: {
    marginLeft: '15px',
    height: '32px',
  },
}));

export const SupplementalAssetsList = ({ asset }: { asset?: Asset }) => {
  const { supplementalAssets, isLoading, error } = useGetSupplementalAssets({
    assetId: asset?.id,
    assetTenantId: asset?.tenantId,
    enabled: !!asset?.hasSupplementalAssets,
  });

  const { deleteSupplementalAssets, isDeleting } = useDeleteSupplementalAssets();
  const [deletingId, setDeletingId] = useState<string>();
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const c = useStyles();

  const handleDelete = (supplementalAssetId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!asset?.id) return;
    setDeletingId(supplementalAssetId);
    deleteSupplementalAssets(
      {
        assetId: asset.id,
        supplementalAssetIds: [supplementalAssetId],
      },
      {
        onSettled: () => setDeletingId(undefined),
      }
    );
  };

  const handleItemClick = (supplementalAssetId: string) => {
    navigate(getPath(`asset/${supplementalAssetId}`));
  };

  if (error) {
    return;
  }

  if (isLoading) {
    return (
      <Skeleton
        data-testid='supplemental-assets-skeleton'
        width={180}
        className={c.SupplementalAssetsList_LoadState}
        variant='text'
      />
    );
  }

  if (!supplementalAssets || supplementalAssets.length === 0) {
    return;
  }

  return (
    <SimpleTreeView
      aria-label='supplemental assets'
      data-testid='supplemental-assets-list'
      className={c.SupplementalAssetsList_TreeView}
    >
      {supplementalAssets.map(supplementalAsset => {
        const asset = supplementalAsset as Asset;
        return (
          <TreeItem
            key={asset.id}
            itemId={asset.id}
            label={
              <Box
                className={c.SupplementalAssetsList_label}
                onClick={event => {
                  event.stopPropagation();
                  handleItemClick(asset.id);
                }}
                sx={{ cursor: 'pointer', color: 'text.primary' }}
              >
                <Box className={c.SupplementalAssetsList_text}>
                  <span className={c.SupplementalAssetsList_filename}>{getAssetFileName(asset)}</span>
                </Box>
                <Tooltip title='Remove Supplemental Asset'>
                  {isDeleting && deletingId === asset.id ? (
                    <IconButton size='small' disabled>
                      <CircularProgress size={16} />
                    </IconButton>
                  ) : (
                    <IconButton
                      className={c.SupplementalAssetsList_deleteButton}
                      onClick={event => handleDelete(asset.id, event)}
                      size='small'
                      color='primary'
                      aria-label={`Remove ${getAssetFileName(asset)}`}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Tooltip>
              </Box>
            }
          />
        );
      })}
    </SimpleTreeView>
  );
};
